import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { saveUser } from "Redux/Slices/User";
import { getUserProfile, updateUserProfile } from "Explore/Services/Settings";
import AvatarUploader from "./AvatarUploader";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { message } from "antd";
import { toast } from "components/ui/toast";
import { Check, RotateCcw, Mail, User } from "lucide-react";

const ProfileSection = () => {
  const dispatch = useDispatch();
  const userStateData = useSelector((state) => state.user?.data);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    avatar: "",
  });

  const [originalData, setOriginalData] = useState({});
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const userStateRef = useRef(userStateData);
  useEffect(() => {
    userStateRef.current = userStateData;
  }, [userStateData]);

  // Fetch latest profile from GET /user/profile on mount
  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      const currentUser = userStateRef.current || {};
      try {
        const profile = await getUserProfile();
        if (isMounted && profile) {
          const profileData = {
            full_name: profile.full_name || "",
            email: profile.email || currentUser.email || "user@example.com",
            avatar: profile.avatar || "",
          };
          setFormData(profileData);
          setOriginalData(profileData);
          // Sync with Redux store
          dispatch(
            saveUser({
              ...currentUser,
              ...profile,
              full_name: profile.full_name,
              name: profile.full_name,
              avatar: profile.avatar,
            })
          );
        }
      } catch (err) {
        console.warn("Failed to fetch /user/profile, falling back to Redux user data:", err);
        if (isMounted) {
          const fallbackData = {
            full_name:
              currentUser.full_name ||
              currentUser.name ||
              [currentUser.first_name, currentUser.last_name].filter(Boolean).join(" ") ||
              "",
            email: currentUser.email || "user@example.com",
            avatar: currentUser.avatar || "",
          };
          setFormData(fallbackData);
          setOriginalData(fallbackData);
        }
      } finally {
        if (isMounted) {
          setFetching(false);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      const dirty = Object.keys(next).some((k) => next[k] !== originalData[k]);
      setIsDirty(dirty);
      return next;
    });
  };

  const handleAvatarUploaded = (newAvatarUrl) => {
    // Backend automatically saved avatar to DB per setting-api-guide.md
    setFormData((prev) => ({ ...prev, avatar: newAvatarUrl }));
    setOriginalData((prev) => ({ ...prev, avatar: newAvatarUrl }));
    setIsDirty(formData.full_name !== originalData.full_name);

    dispatch(
      saveUser({
        ...(userStateRef.current || {}),
        avatar: newAvatarUrl,
      })
    );
  };

  const handleReset = () => {
    setFormData(originalData);
    setIsDirty(false);
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    const trimmedName = formData.full_name.trim();

    if (!trimmedName) {
      toast.error("Full name cannot be empty.", { title: "Validation Error" });
      try { message.error("Full name cannot be empty."); } catch (e) { }
      return;
    }

    if (trimmedName.length > 100) {
      toast.error("Full name exceeds maximum allowed length of 100 characters.", { title: "Validation Error" });
      try { message.error("Full name exceeds maximum allowed length of 100 characters."); } catch (e) { }
      return;
    }

    setSaving(true);
    try {
      const payload = {
        full_name: trimmedName,
        avatar: formData.avatar,
      };

      const res = await updateUserProfile(payload);
      const isSuccess = Boolean(
        res &&
        (res.status === "success" ||
          res.data ||
          res.id ||
          res.full_name ||
          res.message)
      );

      if (isSuccess) {
        const successMsg = res.message || "Profile updated successfully!";
        const updatedUser = {
          ...(userStateRef.current || {}),
          ...(res.data || {}),
          full_name: trimmedName,
          name: trimmedName,
          avatar: formData.avatar,
        };

        dispatch(saveUser(updatedUser));
        setOriginalData(formData);
        setIsDirty(false);

        // Toast notification
        toast.success(successMsg, { title: "Profile Updated" });
        try { message.success(successMsg); } catch (e) { }
      } else {
        throw new Error(res?.message || res?.detail || "Failed to update profile.");
      }
    } catch (err) {
      console.error("Profile update failed:", err);
      const errorDetail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update profile. Please try again.";

      // Toast error notification
      toast.error(errorDetail, { title: "Update Failed" });
      try { message.error(errorDetail); } catch (e) { }
    } finally {
      setSaving(false);
    }
  };

  const userInitials =
    formData.full_name
      .trim()
      .split(" ")
      .map((part) => part[0])
      .filter(Boolean)
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";

  if (fetching) {
    return (
      <div className="max-w-4xl space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-80" />
        </div>
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 tracking-tight">Profile</h2>
        <p className="text-sm text-gray-500 mt-1">
          Manage your personal information and profile photo.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Avatar Uploader Card */}
        <AvatarUploader
          avatarUrl={formData.avatar}
          userInitials={userInitials}
          onAvatarChange={(newUrl) => handleChange("avatar", newUrl)}
          onUploadSuccess={handleAvatarUploaded}
        />

        {/* Personal Information */}
        <Card className="border-gray-200 shadow-2xs">
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold text-gray-900">Personal Information</CardTitle>
            <CardDescription className="text-xs text-gray-500">
              Your name and primary identification across reports and collaboration views.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                <User size={13} className="text-gray-400" />
                Full Name *
              </label>
              <Input
                value={formData.full_name}
                onChange={(e) => handleChange("full_name", e.target.value)}
                placeholder="e.g. Dr. Alex Johnson"
                className="h-9 text-sm"
                maxLength={100}
                required
              />
              <div className="flex justify-between items-center text-[11px] text-gray-400">
                <span>Maximum 100 characters</span>
                <span>{formData.full_name.length}/100</span>
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                  <Mail size={13} className="text-gray-400" />
                  Email Address
                </label>
                <Badge variant="secondary" className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Verified & Active
                </Badge>
              </div>
              <Input
                value={formData.email}
                disabled
                className="h-9 text-sm bg-gray-50 text-gray-500 border-gray-200 cursor-not-allowed"
              />
              <p className="text-[11px] text-gray-400">
                Email is tied to your institutional single sign-on (SSO) account. Contact your administrator to modify.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Sticky Action Bar */}
        <div className="flex items-center justify-between pt-2">
          {isDirty ? (
            <p className="text-xs text-amber-600 font-medium">You have unsaved changes</p>
          ) : (
            <p className="text-xs text-gray-400">All changes saved</p>
          )}

          <div className="flex items-center gap-2">
            {isDirty && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={saving}
                className="gap-1.5 text-xs h-9"
              >
                <RotateCcw size={14} />
                Reset
              </Button>
            )}

            <Button
              type="submit"
              size="sm"
              disabled={!isDirty || saving}
              className="gap-1.5 text-xs h-9 bg-gray-900 hover:bg-gray-800 text-white shadow-2xs"
            >
              <Check size={14} />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProfileSection;
